import React, { FC, useCallback, useMemo, useState } from 'react'
import { usePopoverStyles } from 'containers/Deal/DealInfo/style'
import { Box, IconButton, MenuItem, Popover, PopoverProps, Typography } from '@material-ui/core'
import { DealsStatus, DealsType } from 'typescript/enums/deals'
import { DEAL_EMAIL_PAYOFF_LETTER_PERM, DEAL_SCHEDULE_PAYMENT_PERM } from 'constants/permissions'
import { IInhouseDealResponse, ISchedulePaymentInitValues } from 'typescript/interfaces/deals'
import { usePermission } from 'hooks/usePermission'
import cn from 'classnames'
import { ReactComponent as CancelIcon } from 'assets/svg/CloseIcon.svg'
import SchedulePaymentForm from 'components/SchedulePaymentForm'
import ModalComponent from 'UI/Modal'
import { useMutation, useQuery, useQueryCache } from 'react-query'
import { createSchedule, deleteAllSchedules, getDeal, getSchedulesByDeal, sendAchAuthorizationForm } from 'services/deals'
import { getClientBankAccounts } from 'services/clients'
import { SOMETHING_WENT_WRONG } from 'constants/errors'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useAdditionalMaterialStyle } from 'containers/MaterialUiContainer/additionalStyles'
import ConfirmationContent from 'UI/Modal/ConfirmationContent'
import { SendAchAuthorizationForm } from 'components/SendAchAuthorizationForm'
import { IBankAccountClient } from 'typescript/interfaces/clients'
import { PaymentFrequencyEnum } from 'typescript/interfaces/paymentFrequency'

interface Props extends PopoverProps {
  deal?: IInhouseDealResponse,
  bankAccounts?: IBankAccountClient[]
}

const SchedulePopover: FC<Props> = ({ anchorEl, open, onClose, deal, bankAccounts, ...rest }) => {
  const classes = useAdditionalMaterialStyle()
  const { enqueueSnackbar } = useSnackbar()
  const { id } = useParams<{ id: string }>()
  const queryCache = useQueryCache()
  const { hasPermission } = usePermission()
  const s = usePopoverStyles()
  const [openSchedule, setOpenSchedule] = useState(false)
  const [openDeleteSchedule, setOpenDeleteSchedule] = useState(false)
  const [openAchAuthConfirm, setOpenAchAuthConfirm] = useState(false)
  const [scheduleValues, setScheduleValues] = useState<ISchedulePaymentInitValues | null>(null)

  const handleClosePopover = useCallback(() => {
    // @ts-ignore
    onClose(new Event('click'), 'backdropClick')
  }, [])

  const disableSchedule = useMemo(() => {
    return deal?.status !== DealsStatus.approved || deal?.type === DealsType.outhouse || !hasPermission(DEAL_SCHEDULE_PAYMENT_PERM)
  }, [deal])

  const { data: dataBankAccounts } = useQuery(
    ['client-bank-accounts', deal?.clientId],
    () =>
      getClientBankAccounts({
        clientId: deal?.clientId || 0,
      }),
    {
      cacheTime: 0,
      retry: false,
      enabled: !!deal?.clientId,
    },
  )

  const [createScheduleMut, { isLoading }] = useMutation(createSchedule, {
    onSuccess: () => {
      queryCache.fetchQuery(['inhouse-deal', id], () => getDeal(id))
      queryCache.fetchQuery(['schedulesDeal', id], () => getSchedulesByDeal(id), {
        retry: false,
        cacheTime: 0,
        enabled: id,
      })
      enqueueSnackbar('Schedule payment successfully created')
      setOpenSchedule(false)

      if(hasPermission(DEAL_EMAIL_PAYOFF_LETTER_PERM)){
        setOpenAchAuthConfirm(true);
      }
    },
    onError: () => {
      enqueueSnackbar(SOMETHING_WENT_WRONG)
    },
  })

  const [deleteAllSchedulesMut, { isLoading: isLoadingDeleteAllSchedules }] = useMutation(deleteAllSchedules, {
    onSuccess: () => {
      queryCache.fetchQuery(['inhouse-deal', id], () => getDeal(id))
      queryCache.fetchQuery(['schedulesDeal', id], () => getSchedulesByDeal(id), {
        retry: false,
        cacheTime: 0,
        enabled: id,
      })
      setOpenDeleteSchedule(false)
      enqueueSnackbar('Schedules successfully deleted')
    },
    onError: () => {
      enqueueSnackbar(SOMETHING_WENT_WRONG)
    },
  })

  const [achAuthFormMut, { isLoading: isLoadingAchAuthForm }] = useMutation(
      ({ dealId, payload }: { dealId: string | number; payload: Parameters<typeof sendAchAuthorizationForm>[1] }) =>
        sendAchAuthorizationForm(dealId, payload),
      {
        onSuccess: () => {
          setOpenAchAuthConfirm(false)
          enqueueSnackbar('ACH Authorization Form sent via DocuSign')
        },
        onError: () => {
          enqueueSnackbar(SOMETHING_WENT_WRONG)
        },
      }
    )

  const handleSubmit = useCallback(
    (values: ISchedulePaymentInitValues) => {
      setScheduleValues(values)

      const payload = {
        ...values,
        bankAccountId: values.bankAccountId || undefined,
        paymentDay: values.paymentFrequency === PaymentFrequencyEnum.Weekly ? values.paymentDay : undefined,
      }
      createScheduleMut(payload)
    },
    [createScheduleMut],
  )

  return (
    <>
      <Popover
        classes={{ paper: s.popover }}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        {...rest}
      >
        <MenuItem
          className={s.menuItem}
          disabled={disableSchedule}
          onClick={() => {
            setOpenSchedule(true)
            handleClosePopover()
          }}
        >
          <Typography variant="h4" color="textSecondary">
            New Schedule
          </Typography>
        </MenuItem>
        <MenuItem
          className={s.menuItem}
          disabled={disableSchedule}
          onClick={() => {
            setOpenDeleteSchedule(true)
            handleClosePopover()
          }}
        >
          <Typography variant="h4" color="textSecondary">
            Delete All Schedules
          </Typography>
        </MenuItem>
      </Popover>
      <ModalComponent open={openSchedule}>
        <Box maxWidth={500}>
          <Box display="flex" justifyContent="flex-end" mt="-1rem" mr="-1rem">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => setOpenSchedule(false)}
              classes={{
                root: cn(classes.closeTrsModalButton),
              }}
            >
              <CancelIcon />
            </IconButton>
          </Box>
          <Typography variant="h2" color="textSecondary">
            Schedule Recurring Payments - Deal #{id}
          </Typography>
          {deal && (
            <Box mt="2rem">
              <SchedulePaymentForm
                endDate={deal.scheduleEndDate}
                bankAccountId={deal.bankAccount?.id || ''}
                bankAccounts={dataBankAccounts?.data || []}
                dealId={id}
                cancel={() => setOpenSchedule(false)}
                submit={handleSubmit}
                loading={isLoading}
              />
            </Box>
          )}
        </Box>
      </ModalComponent>
      <ModalComponent open={openDeleteSchedule}>
        <ConfirmationContent
          isLoading={isLoadingDeleteAllSchedules}
          handleCancel={() => setOpenDeleteSchedule(false)}
          handleConfirm={() => deleteAllSchedulesMut(id)}
          text="Are you sure you want to delete all schedules?"
        />
      </ModalComponent>
      <ModalComponent open={openAchAuthConfirm}>
        <SendAchAuthorizationForm
          defaultAmount={scheduleValues?.frequentPayment}
          defaultBankAccountId={scheduleValues?.bankAccountId}
          defaultAchEffectiveDate={deal?.startDate}
          bankAccounts={bankAccounts}
          isLoading={isLoadingAchAuthForm}
          cancel={() => {
            setOpenAchAuthConfirm(false)
            setScheduleValues(null)
          }}
          submit={(payload) => achAuthFormMut({ dealId: id, payload })}
        />
      </ModalComponent>
    </>
  )
}

export default SchedulePopover
